import * as React from 'react';
import clsx from 'clsx';
import { toast as toast$1, useToasterStore, Toaster, ToastBar } from 'react-hot-toast';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function styleInject(css, ref) {
  if (ref === void 0) ref = {};
  var insertAt = ref.insertAt;

  if (!css || typeof document === 'undefined') {
    return;
  }

  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';

  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }

  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z = ".Toast-module_toastIcon__3CYIV{margin-right:10px}.Toast-module_iconMessageWrapper__1VGB-{display:flex;align-items:flex-start}.Toast-module_ellipsis__1FCqH{overflow:hidden;text-overflow:ellipsis}.Toast-module_toastBar__Zb6oC{display:flex;flex-direction:column;align-items:flex-start;line-height:var(--size-line-height-text-body)}.Toast-module_messageActionWrapper__1REPb{display:flex;flex-direction:column;align-items:flex-start;justify-content:space-between}.Toast-module_toastAction__He5PI{margin-top:4px;background-color:transparent;font-weight:var(--font-weight-body-bold);font-size:var(--size-font-body-sm,14px);display:block;border:none;-webkit-appearance:none;-moz-appearance:none;appearance:none;outline:none;color:var(--color-interactive-on-foreground);text-transform:capitalize}.Toast-module_toastAction__He5PI:hover{color:#4c8df6}.Toast-module_toastAction__He5PI:focus{color:#0b5fef}@supports (-webkit-line-clamp:2){.Toast-module_ellipsis__1FCqH{display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical}}@media (min-width:768px){.Toast-module_toastBar__Zb6oC{flex-direction:row}.Toast-module_messageActionWrapper__1REPb{flex-direction:row;align-items:flex-start}.Toast-module_toastAction__He5PI{margin-top:0;margin-left:12px}}";
var styles = {"toastIcon":"Toast-module_toastIcon__3CYIV","iconMessageWrapper":"Toast-module_iconMessageWrapper__1VGB-","ellipsis":"Toast-module_ellipsis__1FCqH","toastBar":"Toast-module_toastBar__Zb6oC","messageActionWrapper":"Toast-module_messageActionWrapper__1REPb","toastAction":"Toast-module_toastAction__He5PI"};
styleInject(css_248z);

var DEFAULT_DURATION = 4000; // 4 seconds

function useToast() {
    return function toast(_a) {
        var message = _a.message, type = _a.type, t = __rest(_a, ["message", "type"]);
        var options = t;
        var toastType = type === 'success' ? 'success' : 'default';
        var duration = options.duration;
        var hotToastOptions = __assign(__assign({}, options), { type: toastType, duration: duration, ellipsis: options.ellipsis });
        return toast$1(message, hotToastOptions);
    };
}
var ToastProvider = function (_a) {
    var children = _a.children, _b = _a.placement, placement = _b === void 0 ? 'top-center' : _b, _c = _a.ellipsis, ellipsis = _c === void 0 ? true : _c, _d = _a.autoDismiss, autoDismiss = _d === void 0 ? true : _d, _e = _a.autoDismissTimeout, autoDismissTimeout = _e === void 0 ? DEFAULT_DURATION : _e, icon = _a.icon, actionLabel = _a.actionLabel, _f = _a.limit, limit = _f === void 0 ? 1 : _f, props = __rest(_a, ["children", "placement", "ellipsis", "autoDismiss", "autoDismissTimeout", "icon", "actionLabel", "limit"]);
    var position = placement || props.position;
    var toasts = useToasterStore().toasts;
    React.useEffect(function () {
        toasts
            .filter(function (t) { return t.visible; }) // Only consider visible toasts
            .filter(function (_, i) { return i >= limit; }) // Is toast index over limit
            .forEach(function (t) { return toast$1.dismiss(t.id); }); // Dismiss – Use toast.remove(t.id) removal without animation
    }, [toasts, limit]);
    var onClickAction = function (t) {
        var _a, _b;
        (_a = t.onActionLabelClicked) === null || _a === void 0 ? void 0 : _a.call(t, t.id);
        toast$1.dismiss(t.id);
        (_b = t.onDismiss) === null || _b === void 0 ? void 0 : _b.call(t, t.id);
    };
    function sanitizeHtmlRemoveScript(htmlString) {
        // Use a regular expression to match and remove script tags.
        var sanitizedHtml = htmlString
            .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
            .replace(/on\w+="[^"]*"/gi, '');
        return sanitizedHtml;
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(Toaster, __assign({}, props, { position: position, toastOptions: {
                duration: autoDismiss ? autoDismissTimeout : Infinity,
            } }), function (t) { return (React.createElement(ToastBar, { toast: t, style: {
                textAlign: 'left',
                background: 'var(--surface-foreground, #2F3031)',
                zIndex: 150,
                fontSize: '0.875rem',
                boxShadow: '0px 2px 10px 0px rgba(0, 0, 0, 0.10), 0px 0px 2px 0px rgba(0, 0, 0, 0.20)',
                borderRadius: 'var(--size-border-radius-base)',
                padding: '0.75rem 1rem 0.75rem 14px',
                color: 'var(--text-inverse, #FFF)',
                maxWidth: '520px',
            } }, function () { return (React.createElement("div", { className: styles.toastBar },
            React.createElement("div", { className: styles.iconMessageWrapper },
                t.type === 'success' && (React.createElement("span", { "data-testid": "toast-icon", className: styles.toastIcon }, t.icon || icon || React.createElement(CheckMarkIcon, null))),
                React.createElement("div", { className: styles.messageActionWrapper },
                    React.createElement("div", { "data-testid": "toast-message", className: clsx((t.ellipsis !== undefined ? t.ellipsis : ellipsis) &&
                            styles.ellipsis), dangerouslySetInnerHTML: {
                            __html: sanitizeHtmlRemoveScript(t.message),
                        } }),
                    (t.actionLabel || actionLabel) && (React.createElement("button", { "data-testid": "toast-action", className: styles.toastAction, onClick: function () { return onClickAction === null || onClickAction === void 0 ? void 0 : onClickAction(t); } }, t.actionLabel || actionLabel)))))); })); }),
        children));
};
var toast = toast$1;
var CheckMarkIcon = function () { return (React.createElement("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
    React.createElement("path", { d: "M8.99997 16.1701L4.82997 12.0001L3.40997 13.4101L8.99997 19.0001L21 7.00009L19.59 5.59009L8.99997 16.1701Z", fill: "#F5F5F5" }))); };

export { ToastProvider, toast, useToast };
